import {
  blogPosts,
  blogPostByPath,
  blogPath,
  BLOG_PATHS,
  BLOG_ARCHIVE_PATHS,
} from '@/config/blog/posts'
import { buildRouteMarkdownBody } from '@/config/agent-markdown'
import { absoluteUrl, CANONICAL_SITE_URL, canonicalUrl, siteConfig } from '@/config/site.config'
import {
  PRESS_RELEASE_PATHS,
  pressReleaseBySlug,
} from '@/config/marketing-content/press-releases.generated'
import { newsroomItems } from '@/config/marketing-content/newsroom-items.config'

export type RouteMeta = {
  title: string
  description: string
  keywords: string
  ogImage?: string
}

export const BASE_PATHS = [
  '/',
  '/features/portfolio-intelligence',
  '/features/market-intelligence',
  '/features/research-intelligence',
  '/features/brokerage-integrations',
  '/pricing',
  '/founding-traders',
  '/about',
  '/support',
  '/newsroom',
  '/blogs',
  '/legal/terms',
  '/legal/privacy',
  '/legal/responsible-use',
  '/legal/platform-disclaimer',
] as const

export type BasePath = (typeof BASE_PATHS)[number]

export const ROUTE_META: Record<BasePath, RouteMeta> = {
  '/': {
    title: 'Podium Markets — Meet Ivy',
    description:
      'Never trade alone again. Ivy is the portfolio-aware AI trading assistant that unifies market data, research, and your brokerage accounts.',
    keywords: 'Podium Markets, Ivy, AI trading, portfolio intelligence, market data',
  },
  '/features/portfolio-intelligence': {
    title: 'Portfolio Intelligence — Podium Markets',
    description:
      'Every account. One source of truth. Ivy connects brokerage accounts for portfolio-aware trading intelligence.',
    keywords: 'portfolio intelligence, brokerage, positions, options exposure',
  },
  '/features/market-intelligence': {
    title: 'Market Intelligence — Podium Markets',
    description: 'Real-time market data and scans filtered through your positions and watchlists.',
    keywords: 'market data, scans, charts, trading',
  },
  '/features/research-intelligence': {
    title: 'Research Intelligence — Podium Markets',
    description: 'SEC filings, earnings, news, and research agents that do the work for you.',
    keywords: 'research, SEC filings, earnings, AI agents',
  },
  '/features/brokerage-integrations': {
    title: 'Brokerage Integrations — Podium Markets',
    description: 'Connect major brokerages securely for portfolio-aware Ivy features.',
    keywords: 'brokerage integrations, connect accounts, trading',
  },
  '/pricing': {
    title: 'Pricing — Podium Markets',
    description:
      'Compare Free, Ivy Portfolio, and Ivy Pro. Three plans, one simple ladder.',
    keywords: 'pricing, plans, Podium Markets',
  },
  '/founding-traders': {
    title: 'Founding Traders — Podium Markets',
    description:
      'A working group for serious traders — early access, direct product influence, and founding pricing held.',
    keywords: 'founding traders, early access, pricing',
  },
  '/about': {
    title: 'About — Podium Markets',
    description:
      'Powered by AI, shaped by traders. The team behind thinkorswim and tradeMONSTER is building Ivy.',
    keywords: 'about, Podium Markets, Ivy, thinkorswim',
  },
  '/support': {
    title: 'Support — Podium Markets',
    description: 'Get help with Ivy, brokerages, billing, and your Podium account.',
    keywords: 'support, help, contact',
  },
  '/blogs': {
    title: 'Blog — Podium Markets',
    description: 'Stories, updates, and perspectives from Podium Markets.',
    keywords: 'daily brief, markets, Ivy, market research',
  },
  '/newsroom': {
    title: 'Newsroom — Podium Markets',
    description:
      'Press releases, media coverage, company announcements, and updates from Podium Markets.',
    keywords: 'newsroom, press releases, media coverage, Podium Markets, Ivy',
  },
  '/legal/terms': {
    title: 'Terms of Service — Podium Markets',
    description: 'Terms governing use of Podium Markets websites and services.',
    keywords: 'terms of service, legal',
  },
  '/legal/privacy': {
    title: 'Privacy Policy — Podium Markets',
    description: 'How Podium Markets collects, uses, and protects your information.',
    keywords: 'privacy policy, legal',
  },
  '/legal/responsible-use': {
    title: 'Responsible Use Policy — Podium Markets',
    description: 'Expectations for safe and lawful use of Podium Markets AI features.',
    keywords: 'responsible use, AI policy, legal',
  },
  '/legal/platform-disclaimer': {
    title: 'Platform Disclaimer & Disclosure — Podium Markets',
    description: 'Platform disclaimer and disclosure information for Podium Markets.',
    keywords: 'disclaimer, disclosure, legal',
  },
}

export type PrerenderPath =
  BasePath | (typeof PRESS_RELEASE_PATHS)[number] | `/blogs/${string}`

export const ALL_PRERENDER_PATHS: PrerenderPath[] = [
  ...BASE_PATHS,
  ...PRESS_RELEASE_PATHS,
  ...BLOG_PATHS,
  ...BLOG_ARCHIVE_PATHS,
]

function getPressReleaseSlug(path: string): string | null {
  const match = path.match(/^\/newsroom\/([^/]+)$/)
  return match?.[1] ?? null
}

export function getRouteMeta(path: PrerenderPath): RouteMeta {
  const post = blogPostByPath[path]
  if (post)
    return {
      title: `${post.seo_title || post.title} — Podium Markets`,
      description: post.seo_description || post.excerpt,
      keywords: post.tags.join(', '),
      ogImage: post.og_image_url || undefined,
    }
  if (BLOG_ARCHIVE_PATHS.includes(path as `/blogs/${string}`))
    return {
      title: `Blog — ${path.slice(7)} — Podium Markets`,
      description: `Blog posts published ${path.slice(7)}.`,
      keywords: 'market archive, daily brief, Ivy',
    }
  if (path in ROUTE_META) {
    return ROUTE_META[path as BasePath]
  }

  const slug = getPressReleaseSlug(path)
  const release = slug ? pressReleaseBySlug[slug] : undefined
  if (!release) {
    throw new Error(`Unknown route meta for path: ${path}`)
  }

  return {
    title: `${release.title} — Podium Markets`,
    description: release.excerpt,
    keywords: 'press release, Podium Markets, Ivy, newsroom',
  }
}

export function routeUrl(path: PrerenderPath): string {
  return absoluteUrl(path === '/' ? '/' : path)
}

export function canonicalRouteUrl(path: PrerenderPath): string {
  return canonicalUrl(path === '/' ? '/' : path)
}

const LLMS_APP_URL = 'https://app.podiummarkets.ai'

const LLMS_SECTIONS: { heading: string; paths: PrerenderPath[] }[] = [
  {
    heading: 'Product',
    paths: ['/', '/pricing', '/founding-traders'],
  },
  {
    heading: 'Features',
    paths: [
      '/features/portfolio-intelligence',
      '/features/market-intelligence',
      '/features/research-intelligence',
      '/features/brokerage-integrations',
    ],
  },
  {
    heading: 'Company',
    paths: ['/about', '/support', '/newsroom', '/blogs', ...BLOG_PATHS],
  },
  {
    heading: 'Legal',
    paths: [
      '/legal/terms',
      '/legal/privacy',
      '/legal/responsible-use',
      '/legal/platform-disclaimer',
    ],
  },
]

function llmsLinkLabel(title: string): string {
  return title.replace(/ — Podium Markets$/, '')
}

function llmsListItem(path: PrerenderPath): string {
  const meta = getRouteMeta(path)
  return `- [${llmsLinkLabel(meta.title)}](${canonicalRouteUrl(path)}): ${meta.description}`
}

export function buildSitemapXml(): string {
  const urlsXml = ALL_PRERENDER_PATHS.map(
    (base) =>
      `  <url>\n    <loc>${canonicalRouteUrl(base)}</loc>\n${blogPostByPath[base] ? `    <lastmod>${blogPostByPath[base].updated_at}</lastmod>\n` : ''}    <changefreq>${base === '/' ? 'weekly' : 'monthly'}</changefreq>\n    <priority>${base === '/' ? '1.0' : '0.5'}</priority>\n  </url>`
  ).join('\n')
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${urlsXml}\n</urlset>\n`
}

/** Content Signals policy (contentsignals.org) — static equivalent of Cloudflare managed robots.txt. */
export function buildRobotsTxt(): string {
  return `# As a condition of accessing this website, you agree to
# abide by the following content signals:
#
# (a)  If a content-signal = yes, you may collect content for the
#      corresponding use.
# (b)  If a content-signal = no, you may not collect content for the
#      corresponding use.
# (c)  If the website operator does not include a content
#      signal for a corresponding use, the website operator
#      neither grants nor restricts permission via content signal
#      with respect to the corresponding use.
#
# The content signals and their meanings are:
# search: building a search index and providing search results
#         (e.g., returning hyperlinks and short excerpts).
#         Search does not include providing AI-generated search summaries.
# ai-input: inputting content into one or more AI models
#           (e.g., retrieval augmented generation, grounding, or
#           other real-time taking of content for generative AI answers).
# ai-train: training or fine-tuning AI models.
#
# ANY RESTRICTIONS EXPRESSED VIA CONTENT SIGNALS ARE EXPRESS
# RESERVATIONS OF RIGHTS UNDER ARTICLE 4 OF THE EUROPEAN
# UNION DIRECTIVE 2019/790 ON COPYRIGHT AND RELATED RIGHTS
# IN THE DIGITAL SINGLE MARKET.

User-agent: *
Content-Signal: search=yes, ai-input=yes, ai-train=no, use=reference
Allow: /

Sitemap: ${CANONICAL_SITE_URL}/sitemap.xml
`
}

/** Per-page markdown for Accept: text/markdown negotiation (static Markdown for Agents). */
export function buildPageMarkdown(path: PrerenderPath): string {
  const meta = getRouteMeta(path)
  const canonical = canonicalRouteUrl(path)
  const title = llmsLinkLabel(meta.title)
  const body = buildRouteMarkdownBody(path)
  const bodyBlock = body ? `\n${body}\n` : `\n${meta.description}\n`
  return `---
title: ${JSON.stringify(meta.title)}
description: ${JSON.stringify(meta.description)}
canonical: ${canonical}
---

# ${title}
${bodyBlock}
- HTML: ${canonical}
- Site index (llms.txt): ${CANONICAL_SITE_URL}/llms.txt
- Sitemap: ${CANONICAL_SITE_URL}/sitemap.xml
- Product app: ${LLMS_APP_URL}
- Help center: https://help.podiummarkets.ai/
`
}

export function markdownPathForRoute(path: PrerenderPath): string {
  return path === '/' ? '/index.md' : `${path}/index.md`
}

export function estimateMarkdownTokens(markdown: string): number {
  return Math.max(1, Math.ceil(markdown.length / 4))
}

/** RFC 9727 api-catalog (application/linkset+json). */
export function buildApiCatalog(): object {
  return {
    linkset: [
      {
        anchor: `${CANONICAL_SITE_URL}/`,
        'service-desc': [
          {
            href: `${CANONICAL_SITE_URL}/.well-known/openapi.json`,
            type: 'application/json',
          },
        ],
        'service-doc': [
          {
            href: `${CANONICAL_SITE_URL}/llms.txt`,
            type: 'text/plain',
          },
          {
            href: 'https://help.podiummarkets.ai/llms.txt',
            type: 'text/plain',
          },
          {
            href: 'https://help.podiummarkets.ai/',
            type: 'text/html',
          },
        ],
        describedby: [
          {
            href: `${CANONICAL_SITE_URL}/sitemap.xml`,
            type: 'application/xml',
          },
        ],
      },
      {
        anchor: LLMS_APP_URL + '/',
        'service-doc': [
          {
            href: `${CANONICAL_SITE_URL}/auth.md`,
            type: 'text/markdown',
          },
        ],
      },
    ],
  }
}

/** Minimal OpenAPI stub — www hosts marketing content, not product REST APIs. */
export function buildOpenApiStub(): object {
  return {
    openapi: '3.1.0',
    info: {
      title: 'Podium Markets public website',
      version: '1.0.0',
      description:
        'Static marketing site for Ivy. Interactive product APIs are served from the authenticated app host, not this origin. Prefer llms.txt and the help center for machine-readable product documentation.',
      contact: {
        name: 'Podium Markets',
        url: CANONICAL_SITE_URL,
      },
    },
    externalDocs: {
      description: 'llms.txt site index',
      url: `${CANONICAL_SITE_URL}/llms.txt`,
    },
    paths: {},
  }
}

export function buildAuthMd(): string {
  return `# auth.md

This host (\`www.podiummarkets.ai\`) is the **public marketing website** for Podium Markets. It does not issue OAuth tokens or host protected APIs.

## For humans

- Sign up / log in: ${LLMS_APP_URL}
- Help center: https://help.podiummarkets.ai/

## For agents

- Site index: ${CANONICAL_SITE_URL}/llms.txt
- API catalog: ${CANONICAL_SITE_URL}/.well-known/api-catalog
- Content preferences: ${CANONICAL_SITE_URL}/robots.txt (Content-Signal)
- Prefer \`Accept: text/markdown\` or the static \`index.md\` sibling of each page

There is no agent registration endpoint on this origin. Product authentication happens on \`app.podiummarkets.ai\`.
`
}

export function buildSiteOverviewSkillMd(): string {
  return `---
name: podium-markets-site
description: Discover Podium Markets marketing pages, pricing, and product docs via llms.txt and the static markdown mirrors.
---

# Podium Markets site

## When to use

Use this skill when answering questions about Ivy, Podium Markets pricing, features, legal pages, or newsroom coverage from the public website.

## Resources

1. Fetch \`${CANONICAL_SITE_URL}/llms.txt\` for the full page index.
2. For a specific page, request the HTML URL with \`Accept: text/markdown\`, or fetch the static \`…/index.md\` sibling (homepage: \`/index.md\`). Mirrors include full legal, pricing, and feature page bodies — not just titles.
3. Respect Content Signals in \`${CANONICAL_SITE_URL}/robots.txt\` (\`ai-train=no\`).
4. Product login/signup is at ${LLMS_APP_URL}, not on www.
5. Help docs: https://help.podiummarkets.ai/llms.txt
`
}

export function buildAgentSkillsIndex(skill: {
  name: string
  description: string
  url: string
  digest: string
}): object {
  return {
    $schema: 'https://schemas.agentskills.io/discovery/0.2.0/schema.json',
    skills: [
      {
        name: skill.name,
        type: 'skill-md',
        description: skill.description,
        url: skill.url,
        digest: skill.digest,
      },
    ],
  }
}

export function buildLlmsTxt(): string {
  const homepageMeta = getRouteMeta('/')
  const sections = LLMS_SECTIONS.map(
    ({ heading, paths }) => `## ${heading}\n${paths.map(llmsListItem).join('\n')}`
  ).join('\n\n')
  const newsLines = newsroomItems
    .map(
      (item) =>
        `- [${item.title}](${item.href}): ${item.excerpt ?? 'Podium Markets newsroom coverage.'}`
    )
    .join('\n')
  const newsSection = newsLines ? `## News\n${newsLines}` : ''

  return `# ${siteConfig.name}

> ${homepageMeta.description}

${siteConfig.name} is the public marketing website for Ivy. Signup and login are at ${LLMS_APP_URL}.

Page URLs support \`Accept: text/markdown\` (or the static \`index.md\` sibling) with full body mirrors of legal, pricing, feature, and marketing content.

Sitemap: ${CANONICAL_SITE_URL}/sitemap.xml

${sections}${newsSection ? `\n\n${newsSection}` : ''}
`
}

export function buildJsonLd(path: PrerenderPath): object {
  const url = routeUrl(path)
  const meta = getRouteMeta(path)
  const ogImage = absoluteUrl(meta.ogImage ?? siteConfig.ogImagePath)
  const slug = getPressReleaseSlug(path)
  const release = slug ? pressReleaseBySlug[slug] : undefined

  const graph: Record<string, unknown>[] = [
    {
      '@type': 'Organization',
      '@id': `${siteConfig.siteUrl}/#organization`,
      name: siteConfig.name,
      url: siteConfig.siteUrl,
      logo: absoluteUrl(siteConfig.appIconPath),
    },
    {
      '@type': 'WebSite',
      '@id': `${siteConfig.siteUrl}/#website`,
      url: siteConfig.siteUrl,
      name: siteConfig.name,
      description: meta.description,
      publisher: { '@id': `${siteConfig.siteUrl}/#organization` },
    },
  ]

  const post = blogPostByPath[path]
  if (post || BLOG_ARCHIVE_PATHS.includes(path as `/blogs/${string}`)) {
    const crumbs = path.split('/').filter(Boolean)
    graph.push({
      '@type': 'BreadcrumbList',
      'itemListElement': crumbs.map((part, index) => ({
        '@type': 'ListItem',
        'position': index + 1,
        'name':
          index === 0
            ? 'Blog'
            : index === crumbs.length - 1 && post
              ? post.title
              : part,
        'item': canonicalRouteUrl(
          `/${crumbs.slice(0, index + 1).join('/')}` as PrerenderPath
        ),
      })),
    })
  }
  if (post) {
    graph.push({
      '@type': 'BlogPosting',
      'headline': post.title,
      'description': post.seo_description || post.excerpt,
      'datePublished': post.published_at,
      'dateModified': post.updated_at,
      'url': canonicalRouteUrl(path),
      'mainEntityOfPage': canonicalRouteUrl(path),
      'author': {
        '@type': post.author.slug === 'ivy' ? 'Organization' : 'Person',
        'name': post.author.name,
      },
      'publisher': { '@id': `${siteConfig.siteUrl}/#organization` },
      'image': ogImage,
    })
  } else if (
    path === '/blogs' ||
    BLOG_ARCHIVE_PATHS.includes(path as `/blogs/${string}`)
  ) {
    graph.push({
      '@type': 'Blog',
      'name': 'Blog',
      'url': canonicalRouteUrl(path),
      'blogPost': blogPosts
        .filter(
          (item) => path === '/blogs' || blogPath(item).startsWith(`${path}/`)
        )
        .map((item) => ({
          '@type': 'BlogPosting',
          'headline': item.title,
          'url': canonicalRouteUrl(blogPath(item)),
        })),
    })
  } else if (release) {
    graph.push({
      '@type': 'NewsArticle',
      headline: release.title,
      description: release.excerpt,
      datePublished: release.date,
      url,
      author: {
        '@type': 'Organization',
        name: siteConfig.name,
      },
      publisher: {
        '@type': 'Organization',
        name: siteConfig.name,
        logo: {
          '@type': 'ImageObject',
          url: absoluteUrl(siteConfig.appIconPath),
        },
      },
      image: ogImage,
    })
  } else {
    graph.push({
      '@type': 'SoftwareApplication',
      name: siteConfig.name,
      applicationCategory: 'FinanceApplication',
      operatingSystem: 'Web',
      url,
      description: meta.description,
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
      image: ogImage,
    })
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  }
}

export function jsonLdScript(path: PrerenderPath): string {
  return JSON.stringify(buildJsonLd(path))
}
